///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { GtthEntityInfoWindowContent } from '../gtth-entity-info-window-content-interface';
import { GtthHistoryService } from '../../engine/history-service';
import { FREQUENCY_SET_AUTOMATICALLY, LinkFeature } from '../../entities/link/link-feature';
import { GtthStepGroupEnum, PdbChannelFrequenciesEnum } from '../../../../generated/shared-types.model';
import { GtthFreezeSelectedLinksCommand } from '../../engine/commands/link/freeze-selected-links-command';
import { SectorFeature } from '../../entities/sector/sector-feature';
import { GtthChangeSectorAngleCommand } from '../../engine/commands/link/change-sector-angle-command';
import { ProductFrequencies } from '../../../../services/product/product-enums/product-frequencies';
import { DeviceSectorsFeature } from '../../entities/sector/device-sectors-feature';
import { GtthChangeFrequenciesMultipleLinksCommand } from '../../engine/commands/link/change-frequencies-multiple-links-command';
import { ProjectProductsService } from '../../engine/project-products-service';
import { GtthInfoWindowsService } from '../info-windows-service';
import { GtthProjectStateService } from '../../engine/project-state.service';
import { FormsModule } from '@angular/forms';
import { GtthAngleInputValidationDirective } from '../../../../shared/directives/validation-directives/angle-input.directive';

@Component({
    selector: 'winde-sector-info',
    templateUrl: './sector-info.component.html',
    styleUrls: ['../entities-info-window.scss'],
    imports: [FormsModule, GtthAngleInputValidationDirective]
})
export class GtthSectorInfoComponent extends GtthEntityInfoWindowContent implements OnInit, OnDestroy {
    public static readonly CUSTOM_ELEMENT_TAG_NAME = 'gtth-sector-info-custom-tag-element';

    @Output()
    public closeCallback = new EventEmitter<void>();

    forceClose = false;

    stepGroupEnum = GtthStepGroupEnum;
    public sector: SectorFeature;
    public deviceSectors: DeviceSectorsFeature;
    public sectorLinks: LinkFeature[];

    public sectorAngle: number;

    public sectorFrequencyChannel = '';
    public frequencySetAutomatically = FREQUENCY_SET_AUTOMATICALLY;
    public sectorAutoFrequencyText = '';
    public sectorFrequency: number;
    public sectorFrequencies: PdbChannelFrequenciesEnum[] = [];
    public unfrozenLinks: LinkFeature[];
    public frozenLinks: LinkFeature[];
    public installedLinks: LinkFeature[];
    public notInstalledLinks: LinkFeature[];
    public sectorAngleMinMax: { min: number; max: number };
    private initialSectorFrequency: number;
    private sectorFrequencyForced: boolean;
    private angleValid = false;
    private oldSectorAngle: number;
    private sectorIndex: number;
    private sectorInDevice: number;

    private modifyLinkFrequencyMultipleCommand: GtthChangeFrequenciesMultipleLinksCommand;

    constructor(
        private readonly projectProductsService: ProjectProductsService,
        public readonly historyService: GtthHistoryService,
        public readonly projectStateService: GtthProjectStateService,
        infoWindowService: GtthInfoWindowsService
    ) {
        super();
        infoWindowService.registerActiveInfoWindow(this);
    }

    ngOnInit(): void {
        [this.sectorIndex, this.sectorInDevice] = SectorFeature.getSectorIndexesFromAggregatedIndex(this.entityId);

        this.sector = this.historyService.logicalTopology.getSector(this.sectorIndex, this.sectorInDevice);
        if (!this.sector) {
            this.closeCallback.next();
            return;
        }
        this.sectorLinks = this.sector.getLinks();
        this.sectorAngle = this.sector.getAngle();
        this.oldSectorAngle = this.sectorAngle;
        this.deviceSectors = this.historyService.logicalTopology.getDeviceSectors(this.sectorIndex);

        this.sectorAngleMinMax = this.deviceSectors.getAngleMinMaxRange(this.sectorInDevice);
        this.initialSectorFrequency = this.sectorLinks[0].getTxFrequency();
        this.sectorFrequencyForced = this.sectorLinks.some((link) => link.isFrequencyForced());
        this.sectorFrequencyChannel = ProductFrequencies.getFrequencyChannelName(this.initialSectorFrequency);

        this.sectorFrequency = this.sectorFrequencyForced ? this.initialSectorFrequency : this.frequencySetAutomatically;
        this.sectorAutoFrequencyText =
            !this.sectorFrequencyForced && this.initialSectorFrequency !== FREQUENCY_SET_AUTOMATICALLY
                ? 'Auto (' + this.initialSectorFrequency + ')'
                : 'Auto';

        const sectorLinkIds = this.sectorLinks.map((link) => link.getId());
        this.modifyLinkFrequencyMultipleCommand = this.historyService
            .command(GtthChangeFrequenciesMultipleLinksCommand)
            .create(this.initialSectorFrequency, this.sectorFrequencyForced, sectorLinkIds, this.sector);
        this.sectorFrequencies = this.projectProductsService.getBaseRadioFrequenciesById(this.sectorLinks[0].getBuUsedProduct());
        this.sortFrozenLinks();
    }

    ngOnDestroy(): void {
        if (this.angleValid === true && this.oldSectorAngle !== this.sectorAngle) {
            this.historyService.doCommand(
                this.historyService
                    .command(GtthChangeSectorAngleCommand)
                    .create(this.sector.getSectorIndex(), this.sector.getSectorInDevice(), this.sector.getAngle())
            );
        } else {
            const sectorDelta = this.sector.getAngle() - this.oldSectorAngle;
            this.deviceSectors.changeAngle(sectorDelta);
        }
        this.historyService.doCommand(this.modifyLinkFrequencyMultipleCommand);
    }

    toggleFreezeLinks(linkList: LinkFeature[], status: boolean, isInstalled: boolean): void {
        const freezeLinksCommand = this.historyService
            .command(GtthFreezeSelectedLinksCommand)
            .createFromLinksList(linkList, status, isInstalled);
        this.historyService.doCommand(freezeLinksCommand).then((resolve: boolean) => {
            if (resolve) {
                if (this.historyService.selectedStepNumber === GtthStepGroupEnum.LOCATIONS_AND_LOSES && !status && !isInstalled) {
                    this.closeCallback.next();
                } else {
                    this.sortFrozenLinks();
                    if (!status) {
                        this.changeSectorFrequency(FREQUENCY_SET_AUTOMATICALLY);
                    }
                }
            }
        });
    }

    isValid(): boolean {
        return true;
    }

    changeAngle(newAngle: number): void {
        this.sectorAngle = newAngle;

        if (this.sectorAngle >= 360) {
            this.sectorAngle = this.sectorAngle % 360;
        } else if (this.sectorAngle < 0) {
            this.sectorAngle += 360;
        }

        this.angleValid = this.deviceSectors.changeSectorsDirection(this.sectorInDevice, this.sectorAngle);
    }

    changeSectorFrequency(frequency: PdbChannelFrequenciesEnum | number): void {
        const frequencyForced = frequency !== 0;
        this.sectorFrequency = frequency;
        this.modifyLinkFrequencyMultipleCommand.setNewLinkFrequency(frequency, frequencyForced, false);
    }

    private sortFrozenLinks(): void {
        this.unfrozenLinks = [];
        this.frozenLinks = [];
        this.installedLinks = [];
        this.notInstalledLinks = [];
        this.sectorLinks.forEach((link: LinkFeature) => {
            if (link.isUserForced()) {
                if (link.isInstalled()) {
                    this.installedLinks.push(link);
                } else {
                    this.notInstalledLinks.push(link);
                    this.frozenLinks.push(link);
                }
            } else {
                this.unfrozenLinks.push(link);
            }
        });
    }
}
